import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, FileDown, Inbox, Loader2, RefreshCw, Archive } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const TABS = [
  { key: "fasttrack", label: "FastTrack" },
  { key: "quotes", label: "Quotes" },
  { key: "buyers", label: "Buyers" },
  { key: "suppliers", label: "Suppliers" },
  { key: "rfqs", label: "RFQs" },
  { key: "contacts", label: "Contacts" },
  { key: "newsletter", label: "Newsletter" },
];
const STATUSES = ["new", "in-progress", "closed"];

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("fasttrack");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { if (user === false) navigate("/login"); }, [user, navigate]);

  const loadStats = useCallback(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  const loadRows = useCallback((kind) => {
    setLoading(true);
    api.get(`/admin/submissions/${kind}`).then((r) => setRows(r.data)).catch(() => setRows([])).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (user && user !== false) { loadStats(); loadRows(tab); }
  }, [user, tab, loadStats, loadRows]);

  const updateStatus = async (id, status) => {
    await api.patch(`/admin/submissions/${tab}/${id}?status=${status}`);
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
    loadStats();
  };

  const downloadFile = async (file) => {
    try {
      const res = await api.get(`/files/${file.storage_path}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = file.original_filename || "download"; a.click();
      URL.revokeObjectURL(url);
    } catch {}
  };

  const [downloadingSource, setDownloadingSource] = useState(false);
  const downloadSource = async () => {
    setDownloadingSource(true);
    try {
      const res = await api.get("/admin/download-source", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "sourcex-global-source.zip"; a.click();
      URL.revokeObjectURL(url);
    } catch {} finally { setDownloadingSource(false); }
  };

  if (!user || user === false)
    return <div className="min-h-screen grid place-items-center bg-background"><Loader2 className="animate-spin text-brand-orange" /></div>;

  const statCards = stats ? [["New", stats.new], ["FastTrack", stats.fasttrack], ["Quotes", stats.quotes], ["Buyers", stats.buyers], ["Suppliers", stats.suppliers], ["Newsletter", stats.newsletter]] : [];

  const detail = (r) => {
    const bits = [];
    if (r.urgency) bits.push(`⚡ Urgency: ${r.urgency}`);
    if (r.services) bits.push(`Services: ${r.services}`);
    if (r.product_name) bits.push(`Product: ${r.product_name}`);
    if (r.country) bits.push(`Country: ${r.country}`);
    if (r.target_country) bits.push(`Target Country: ${r.target_country}`);
    if (r.delivery_date) bits.push(`Delivery: ${r.delivery_date}`);
    if (r.commodity) bits.push(`Commodity: ${r.commodity}`);
    if (r.quantity) bits.push(`Qty: ${r.quantity}`);
    if (r.annual_requirement) bits.push(`Annual: ${r.annual_requirement}`);
    if (r.target_price) bits.push(`Target: ${r.target_price}`);
    if (r.product_category) bits.push(`Categories: ${r.product_category}`);
    if (r.manufacturing_location) bits.push(`Location: ${r.manufacturing_location}`);
    if (r.certifications) bits.push(`Certs: ${r.certifications}`);
    if (r.export_experience) bits.push(`Export: ${r.export_experience}`);
    if (r.production_capacity) bits.push(`Capacity: ${r.production_capacity}`);
    if (r.gst) bits.push(`GST: ${r.gst}`);
    if (r.mobile) bits.push(`Mobile: ${r.mobile}`);
    if (r.whatsapp) bits.push(`WhatsApp: ${r.whatsapp}`);
    if (r.phone) bits.push(`Phone: ${r.phone}`);
    if (r.additional_requirements) bits.push(r.additional_requirements);
    return bits;
  };

  return (
    <div data-testid="admin-page" className="min-h-screen bg-secondary pt-16 md:pt-20">
      <div className="bg-navy text-white">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 py-6 flex items-center justify-between">
          <div>
            <p className="overline mb-1">Admin Dashboard</p>
            <h1 className="font-display font-black text-2xl">Submissions</h1>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button onClick={downloadSource} disabled={downloadingSource} data-testid="download-source-btn"
              className="inline-flex items-center gap-2 border border-white/20 px-4 py-2 font-mono uppercase text-[10px] tracking-[0.2em] hover:bg-white/10 transition-colors disabled:opacity-60">
              {downloadingSource ? <Loader2 size={14} className="animate-spin" /> : <Archive size={14} />} Source ZIP</button>
            <button onClick={() => { loadStats(); loadRows(tab); }} data-testid="refresh-btn"
              className="inline-flex items-center gap-2 border border-white/20 px-4 py-2 font-mono uppercase text-[10px] tracking-[0.2em] hover:bg-white/10 transition-colors"><RefreshCw size={14} /> Refresh</button>
            <button onClick={() => { logout(); navigate("/login"); }} data-testid="logout-btn"
              className="inline-flex items-center gap-2 bg-brand-orange px-4 py-2 font-mono uppercase text-[10px] tracking-[0.2em] hover:bg-brand-orangeHover transition-colors"><LogOut size={14} /> Logout</button>
          </div>
        </div>
      </div>

      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 py-8">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-px bg-border border border-border mb-8">
          {statCards.map(([l, n]) => (
            <div key={l} className="bg-card p-6">
              <div className="font-display font-black text-3xl md:text-4xl">{n}</div>
              <div className="font-mono uppercase text-[10px] tracking-[0.15em] text-muted-foreground mt-1">{l}</div>
            </div>
          ))}
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-card border border-border rounded-none p-0 h-auto flex-wrap">
            {TABS.map((t) => (
              <TabsTrigger key={t.key} value={t.key} data-testid={`tab-${t.key}`}
                className="rounded-none font-mono uppercase text-[11px] tracking-[0.15em] px-6 py-3 data-[state=active]:bg-brand-orange data-[state=active]:text-white">
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {TABS.map((t) => (
            <TabsContent key={t.key} value={t.key} className="mt-6">
              {loading ? (
                <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-brand-orange" /></div>
              ) : rows.length === 0 ? (
                <div className="border border-border bg-card p-16 text-center">
                  <Inbox className="mx-auto text-muted-foreground mb-4" size={36} />
                  <p className="font-mono uppercase text-xs tracking-[0.2em] text-muted-foreground">No submissions yet</p>
                </div>
              ) : (
                <div className="space-y-px bg-border border border-border" data-testid="submissions-list">
                  {rows.map((r) => (
                    <div key={r.id} className="bg-card p-6 grid md:grid-cols-12 gap-4 items-start">
                      <div className="md:col-span-3">
                        <div className="font-display font-bold">{r.company_name || r.name || r.email}</div>
                        {r.contact_person && <div className="text-muted-foreground text-sm">{r.contact_person}</div>}
                        <a href={`mailto:${r.email}`} className="text-brand-orange text-sm break-all">{r.email}</a>
                      </div>
                      <div className="md:col-span-5 text-sm text-muted-foreground space-y-1">
                        {detail(r).map((b) => <p key={b}>{b}</p>)}
                        {r.message && <p className="line-clamp-2">{r.message}</p>}
                        <div className="flex flex-wrap gap-3">
                          {r.file && <button onClick={() => downloadFile(r.file)} data-testid={`file-${r.id}`} className="inline-flex items-center gap-1.5 text-brand-orange hover:underline"><FileDown size={14} /> {r.file.original_filename}</button>}
                          {r.catalogue && <button onClick={() => downloadFile(r.catalogue)} className="inline-flex items-center gap-1.5 text-brand-orange hover:underline"><FileDown size={14} /> {r.catalogue.original_filename}</button>}
                        </div>
                      </div>
                      <div className="md:col-span-2 text-xs font-mono text-muted-foreground">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}</div>
                      <div className="md:col-span-2">
                        {t.key !== "newsletter" && (
                          <select value={r.status || "new"} onChange={(e) => updateStatus(r.id, e.target.value)} data-testid={`status-${r.id}`}
                            className="w-full border border-border px-3 py-2 rounded-none font-mono uppercase text-[10px] tracking-[0.1em] bg-card focus:outline-none focus:ring-2 focus:ring-brand-orange">
                            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
}
